import { Box, Menu, MenuItem, Stack, Tooltip, Typography } from '@mui/material';
import { EditIcon } from 'lucide-react';
import { enqueueSnackbar } from 'notistack';
import { useCallback, useMemo, useState } from 'react';
import { useIntl } from 'react-intl';
import { useRecoilValue } from 'recoil';
import ReviewStatus from '~/components/Structures/ReviewStatus';
import {
    AVAILABLE_ROI_TYPES,
    ROI_POINT_TYPES,
    RoiInterpretedType,
} from '~/domain/dicom/dicomstructureset';
import { RoiReadModel } from '~/domain/readmodels/StructureSetReadModel';
import { currentStructureSetState } from '~/state/atoms/structureSet/structureSetAtoms';
import PointCoordinateEditor from './PointCoordinateEditor';

export default function StructureInformation({
    roi,
    onInterpretedTypeChange,
    onPointCoordinatesChange,
    isEditable,
    getRoiVolumeCm3,
}: {
    roi: RoiReadModel;
    onInterpretedTypeChange: (value: RoiInterpretedType) => void;
    onPointCoordinatesChange: (coords: {
        x: number;
        y: number;
        z: number;
    }) => void;
    isEditable: boolean;
    getRoiVolumeCm3: number;
}) {
    const intl = useIntl();
    const currentStructureSet = useRecoilValue(currentStructureSetState);
    const liveRoi = currentStructureSet?.roiArray.find(
        (r) => r.roiNumber === roi.roiNumber
    );
    const liveCoordinatesMm =
        liveRoi?.pointCoordinatesMm ?? roi.pointCoordinatesMm;

    const [typeMenuAnchor, setTypeMenuAnchor] = useState<null | HTMLElement>(
        null
    );

    // Prevent switching between point and volumetric categories.
    const currentTypeIsPoint = roi.isPointRoi;

    // Only one EXTERNAL (body contour) is allowed per structure set.
    const existingExternalRoi = useMemo(
        () =>
            currentStructureSet?.roiArray.find(
                (r) =>
                    r.roiNumber !== roi.roiNumber &&
                    r.interpretedType === RoiInterpretedType.EXTERNAL
            ) ?? null,
        [currentStructureSet, roi.roiNumber]
    );

    const handleTypeChange = useCallback(
        (newType: RoiInterpretedType) => {
            if (
                newType === RoiInterpretedType.EXTERNAL &&
                existingExternalRoi
            ) {
                enqueueSnackbar(
                    `An EXTERNAL (${existingExternalRoi.name}) ` +
                        'structure already exists.',
                    { variant: 'warning' }
                );
                return;
            }
            onInterpretedTypeChange(newType);
        },
        [onInterpretedTypeChange, existingExternalRoi]
    );

    const allowedTypes = useMemo(() => {
        const categoryTypes = currentTypeIsPoint
            ? ROI_POINT_TYPES
            : AVAILABLE_ROI_TYPES.filter((t) => !ROI_POINT_TYPES.includes(t));

        return categoryTypes.includes(roi.interpretedType)
            ? categoryTypes
            : [roi.interpretedType, ...categoryTypes];
    }, [roi.interpretedType, currentTypeIsPoint]);

    const volumeLabel = `${getRoiVolumeCm3.toFixed(2)} cm\u00B3`;
    const algorithmLabel = roi.roiGenerationAlgorithm;

    return (
        <Stack display="grid">
            <Box
                display="flex"
                alignItems="center"
                flexWrap="wrap"
                sx={{ minWidth: 0, overflow: 'hidden' }}
            >
                <Box
                    display="flex"
                    alignItems="center"
                    gap={0.5}
                    sx={{ whiteSpace: 'nowrap', mr: 1 }}
                >
                    <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{ fontSize: 11, flexShrink: 0 }}
                    >
                        {intl.formatMessage({ id: 'structure.type' })}:
                    </Typography>
                    <Typography
                        variant="caption"
                        sx={{ fontSize: 11, flexShrink: 0 }}
                        data-testid="structure-type-value"
                    >
                        {roi.interpretedType ||
                            intl.formatMessage({ id: 'unknown' })}
                    </Typography>
                    {isEditable && (
                        <Box
                            component="span"
                            onClick={(e) => setTypeMenuAnchor(e.currentTarget)}
                            sx={{
                                display: 'inline-flex',
                                flexShrink: 0,
                                cursor: 'pointer',
                                color: 'text.secondary',
                                '&:hover': { color: 'primary.main' },
                            }}
                            data-testid="structure-type-edit-button"
                        >
                            <EditIcon size={10} />
                        </Box>
                    )}
                </Box>
                {isEditable && (
                    <Menu
                        anchorEl={typeMenuAnchor}
                        open={Boolean(typeMenuAnchor)}
                        onClose={() => setTypeMenuAnchor(null)}
                    >
                        {allowedTypes.map((value) => {
                            const isBlockedExternal =
                                value === RoiInterpretedType.EXTERNAL &&
                                roi.interpretedType !==
                                    RoiInterpretedType.EXTERNAL &&
                                existingExternalRoi !== null;

                            const item = (
                                <MenuItem
                                    key={value || 'UNKNOWN'}
                                    selected={value === roi.interpretedType}
                                    disabled={isBlockedExternal}
                                    onClick={() => {
                                        handleTypeChange(value);
                                        setTypeMenuAnchor(null);
                                    }}
                                >
                                    {value || 'Unknown'}
                                </MenuItem>
                            );

                            if (isBlockedExternal) {
                                return (
                                    <Tooltip
                                        key={value || 'UNKNOWN'}
                                        title={`An EXTERNAL (${existingExternalRoi?.name}) structure already exists`}
                                        placement="right"
                                    >
                                        <span>{item}</span>
                                    </Tooltip>
                                );
                            }
                            return item;
                        })}
                    </Menu>
                )}
                {roi.identificationCode && (
                    <Box
                        display="flex"
                        gap={0.5}
                        overflow="hidden"
                        sx={{ whiteSpace: 'nowrap' }}
                    >
                        <Typography
                            variant="caption"
                            color="text.secondary"
                            sx={{
                                fontSize: 11,
                                flexShrink: 0,
                            }}
                        >
                            {intl.formatMessage({
                                id: 'structure.identificationCode',
                            })}
                            :
                        </Typography>

                        <Typography
                            variant="caption"
                            noWrap
                            sx={{
                                fontSize: 11,
                                overflow: 'hidden',
                            }}
                            data-testid="structure-identification-code-value"
                        >
                            {roi.identificationCode.codeMeaning}
                        </Typography>
                    </Box>
                )}
            </Box>
            <Box display="flex" alignItems="center" flexWrap="wrap" gap={0.5}>
                {!roi.isPointRoi && (
                    <>
                        <Box
                            display="flex"
                            alignItems="center"
                            gap={0.5}
                            sx={{ whiteSpace: 'nowrap' }}
                        >
                            <Box lineHeight={0}>
                                <ReviewStatus
                                    status={roi.approvalStatus}
                                    fontSize={11}
                                    color="text.secondary"
                                />
                            </Box>
                            <Typography
                                variant="caption"
                                color="text.secondary"
                                sx={{ fontSize: 11 }}
                            >
                                /
                            </Typography>
                        </Box>
                        <Box
                            display="flex"
                            alignItems="center"
                            gap={0.5}
                            sx={{ whiteSpace: 'nowrap' }}
                        >
                            <Typography
                                variant="caption"
                                color="text.secondary"
                                sx={{ fontSize: 11 }}
                                data-testid="structure-volume-value"
                            >
                                {volumeLabel}
                            </Typography>
                            <Typography
                                variant="caption"
                                color="text.secondary"
                                sx={{ fontSize: 11 }}
                            >
                                /
                            </Typography>
                        </Box>
                    </>
                )}
                <Typography
                    variant="caption"
                    color="text.secondary"
                    noWrap
                    sx={{ fontSize: 11 }}
                >
                    {algorithmLabel}
                </Typography>
            </Box>
            {roi.isPointRoi && liveCoordinatesMm != null && (
                <>
                    <Box sx={{ my: 0.5 }} />
                    <PointCoordinateEditor
                        coordinatesMm={liveCoordinatesMm}
                        onChange={onPointCoordinatesChange}
                        disabled={!isEditable}
                    />
                </>
            )}
        </Stack>
    );
}
